import type { ViewKey } from '@view-model/domain/key';
import type { CommandManager } from '@model-framework/command';
import { CommandHelper, CompositeCommand, CreateCommand } from '@model-framework/command';
import type { ConsistencyLinkCollection } from '@view-model/domain/ConsistencyLink/ConsistencyLinkCollection';
import { ConsistencyLink } from '@view-model/domain/ConsistencyLink';
import { ObjectRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import type { ConsistencyLinkId, ViewModelId } from 'balus-common';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { ViewCollectionContents } from '@view-model/domain/view/ViewCollectionContents';
import { ConsistencyLinkPlacement } from '@view-model/models/sticky/ConsistencyLink/domain/ConsistencyLinkPlacement';

export class ConsistencyLinksOperation {
  static handleCreate(params: {
    fromViewKey: ViewKey;
    toViewKey: ViewKey;
    viewModelId: ViewModelId;
    commandManager: CommandManager;
  }): void {
    const consistencyLink = ConsistencyLink.buildNew(params.fromViewKey, params.toViewKey);

    const repo = new ObjectRepository(
      ConsistencyLink,
      RTDBPath.ConsistencyLink.linkPath(params.viewModelId, consistencyLink.id)
    );
    const command = new CreateCommand(consistencyLink, repo);
    params.commandManager.execute(command);
  }

  static async handleDelete(params: {
    ids: Set<ConsistencyLinkId>;
    viewModelId: ViewModelId;
    commandManager: CommandManager;
  }): Promise<void> {
    const paths: string[] = [];
    params.ids.forEach((id) => {
      paths.push(RTDBPath.ConsistencyLink.linkPath(params.viewModelId, id));
    });
    const multiCommand = await CommandHelper.buildDeleteCommandMulti(...paths);
    const commands = CompositeCommand.composeOptionalCommands(...multiCommand.flat());
    if (commands) {
      params.commandManager.execute(commands);
    }
  }

  static filterByRect(
    links: ConsistencyLinkCollection,
    viewContents: ViewCollectionContents,
    selectionRect: Rect
  ): Set<ConsistencyLinkId> {
    const selectedLinkIds: string[] = [];

    for (const link of links.toArray()) {
      const linkPlacement = this.createLinkPlacement(link, viewContents);
      if (!linkPlacement) continue;

      const bezierLine = linkPlacement.bezierLine();
      if (bezierLine.intersects(selectionRect)) {
        selectedLinkIds.push(link.id);
      }
    }

    return new Set(selectedLinkIds);
  }

  private static createLinkPlacement(
    link: ConsistencyLink,
    viewContents: ViewCollectionContents
  ): ConsistencyLinkPlacement | null {
    // リンクしているViewオブジェクトからPositionを探す（from, to）
    const fromPosition = viewContents.getCenterPoint(link.from.id);
    const toPosition = viewContents.getCenterPoint(link.to.id);
    if (fromPosition === null || toPosition === null) {
      return null;
    }

    return new ConsistencyLinkPlacement(fromPosition, toPosition);
  }
}
